import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Paper,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Divider,
  Chip,
  Box,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add,
  Delete,
  CloudUpload,
  Close,
  Phone,
  Link as LinkIcon,
  Reply,
  Code,
  Info,
  PlayArrow,
  List as ListIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  "@global": {
    "@keyframes slideIn": {
      from: {
        opacity: 0,
        transform: "translateY(-10px)",
      },
      to: {
        opacity: 1,
        transform: "translateY(0)",
      },
    },
  },
  dialogPaper: {
    minHeight: "85vh",
    maxHeight: "95vh",
    maxWidth: "1200px",
  },
  dialogContent: {
    display: "flex",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      gap: theme.spacing(2),
    },
  },
  formSection: {
    flex: 1,
    overflowY: "auto",
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      paddingRight: 0,
    },
  },
  previewSection: {
    flex: "0 0 380px",
    position: "sticky",
    top: 0,
    height: "fit-content",
    [theme.breakpoints.down("md")]: {
      position: "relative",
      flex: "1 1 auto",
    },
  },
  previewContainer: {
    backgroundColor: "#d9d3c7",
    padding: theme.spacing(3),
    borderRadius: 12,
    minHeight: 200,
    position: "relative",
  },
  previewTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  previewPlayButton: {
    minWidth: "auto",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 6,
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  previewMessage: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: theme.spacing(1.5, 2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    maxWidth: 280,
    position: "relative",
  },
  previewHeader: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: "#000",
    marginBottom: theme.spacing(1),
  },
  previewBody: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.875rem",
    color: "#000",
    marginBottom: theme.spacing(0.5),
  },
  previewFooter: {
    fontSize: "0.75rem",
    color: "#8696a0",
    marginTop: theme.spacing(1),
  },
  previewTime: {
    fontSize: "0.6875rem",
    color: "#8696a0",
    textAlign: "right",
    marginTop: theme.spacing(0.5),
  },
  previewButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    backgroundColor: "#f0f2f5",
    borderRadius: 4,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#e4e6eb",
      transform: "scale(1.02)",
    },
  },
  previewListMenu: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    backgroundColor: "#00a884",
    color: "#fff",
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: 500,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#008f6f",
      transform: "scale(1.02)",
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  uploadButton: {
    marginTop: theme.spacing(1),
  },
  buttonList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  buttonItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: "1px solid #e0e0e0",
    borderRadius: 4,
  },
  variableChip: {
    margin: theme.spacing(0.5),
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.05)",
      boxShadow: theme.shadows[2],
    },
  },
  charCounter: {
    fontSize: "0.75rem",
    color: "#666",
    textAlign: "right",
    marginTop: theme.spacing(0.5),
  },
  removeHeaderButton: {
    backgroundColor: "#f44336",
    color: "#fff",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#d32f2f",
    },
  },
  variablesContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  variablesList: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  customVariableInput: {
    marginTop: theme.spacing(2),
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: "#e3f2fd",
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  variableExample: {
    fontFamily: "monospace",
    backgroundColor: "#f5f5f5",
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: "0.875rem",
  },
}));

// Variáveis padrão disponíveis para campanhas com listas de contatos
const DEFAULT_VARIABLES = [
  { key: "nome", label: "Nome", description: "Nome do contato (ou {{1}})" },
  { key: "email", label: "Email", description: "Email do contato (ou {{2}})" },
  { key: "campo1", label: "Campo 1", description: "Campo personalizado 1 (ou {{3}})" },
  { key: "campo2", label: "Campo 2", description: "Campo personalizado 2 (ou {{4}})" },
  { key: "campo3", label: "Campo 3", description: "Campo personalizado 3 (ou {{5}})" },
  { key: "campo4", label: "Campo 4", description: "Campo personalizado 4 (ou {{6}})" },
  { key: "campo5", label: "Campo 5", description: "Campo personalizado 5 (ou {{7}})" },
];

const TemplateModal = ({ open, onClose, templateId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [template, setTemplate] = useState({
    name: "",
    channel: "",
    category: "MARKETING",
    language: "pt_BR",
    headerType: "NONE", // NONE, TEXT, IMAGE, VIDEO, DOCUMENT
    headerText: "",
    headerMedia: null,
    bodyText: "",
    footerText: "",
    buttons: [], // Máximo 3 botões
    variables: [],
  });

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAllButtons, setShowAllButtons] = useState(false);

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data } = await api.get("/whatsapp", {
          params: { session: 0 },
        });
        setConnections(data.filter((w) => w.channel === "whatsapp_oficial"));
      } catch (err) {
        toast.error("Erro ao carregar conexões");
      }
    };
    fetchConnections();
  }, []);

  useEffect(() => {
    if (templateId) {
      const fetchTemplate = async () => {
        try {
          const { data } = await api.get(`/whatsapp-templates/${templateId}`);
          
          // Determinar o tipo de header baseado nos dados
          let headerType = "NONE";
          if (data.headerText) {
            headerType = "TEXT";
          } else if (data.headerMedia) {
            // Detectar tipo de mídia pela URL ou extensão
            const mediaUrl = data.headerMedia.toLowerCase();
            if (mediaUrl.includes('.mp4') || mediaUrl.includes('.mov') || mediaUrl.includes('video')) {
              headerType = "VIDEO";
            } else if (mediaUrl.includes('.pdf') || mediaUrl.includes('.doc') || mediaUrl.includes('document')) {
              headerType = "DOCUMENT";
            } else {
              headerType = "IMAGE"; // padrão para imagens
            }
          }
          
          // Garantir que todos os campos sejam strings ou arrays
          const templateData = {
            ...data,
            channel: data.channelName || data.channel || "",  // channelName do banco → channel do form
            headerText: data.headerText || "",
            headerMedia: data.headerMedia || null,
            bodyText: data.bodyText || "",
            footerText: data.footerText || "",
            buttons: data.buttons || [],
            headerType: headerType,
          };
          
          console.log("📝 Template carregado:", templateData);
          console.log("🖼️ Header Type:", headerType);
          console.log("📎 Header Media:", data.headerMedia);
          
          setTemplate(templateData);
        } catch (err) {
          toast.error("Erro ao carregar template");
        }
      };
      fetchTemplate();
    } else {
      setTemplate({
        name: "",
        channel: "",
        category: "MARKETING",
        language: "pt_BR",
        headerType: "TEXT",
        headerText: "",
        headerMedia: null,
        bodyText: "",
        footerText: "",
        buttons: [],
      });
    }
  }, [templateId, open]);

  const handleSave = async () => {
    if (!template.name || !template.channel || !template.bodyText) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }

    // Validar proporção de variáveis
    const bodyValidation = validateVariablesRatio(template.bodyText);
    const headerValidation = template.headerText ? validateVariablesRatio(template.headerText) : { valid: true };
    
    if (!bodyValidation.valid || !headerValidation.valid) {
      toast.error(bodyValidation.warning || headerValidation.warning);
      return;
    }

    setLoading(true);
    try {
      if (templateId) {
        await api.put(`/whatsapp-templates/${templateId}`, template);
        toast.success("Template atualizado com sucesso!");
      } else {
        await api.post("/whatsapp-templates", template);
        toast.success("Template criado e enviado para aprovação!");
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao salvar template");
    }
    setLoading(false);
  };

  const handleAddButton = (type) => {
    // Quick Reply: máximo 3
    // Call to Action (URL/PHONE): máximo 2
    // Se misturar, máximo 3 no total
    const quickReplyCount = template.buttons.filter(b => b.type === "QUICK_REPLY").length;
    const callToActionCount = template.buttons.filter(b => ["URL", "PHONE_NUMBER", "COPY_CODE"].includes(b.type)).length;
    
    if (type === "QUICK_REPLY" && quickReplyCount >= 3) {
      toast.warning("Máximo de 3 botões Quick Reply permitidos");
      return;
    }
    
    if (["URL", "PHONE_NUMBER", "COPY_CODE"].includes(type) && callToActionCount >= 2) {
      toast.warning("Máximo de 2 botões de Call to Action permitidos");
      return;
    }
    
    if (template.buttons.length >= 10) {
      toast.warning("Máximo de 10 botões permitidos");
      return;
    }
    
    const newButton = {
      id: Date.now(),
      type,
      text: "",
      value: "",
    };
    setTemplate({ ...template, buttons: [...template.buttons, newButton] });
  };

  const handleRemoveButton = (buttonId) => {
    setTemplate({
      ...template,
      buttons: template.buttons.filter((b) => b.id !== buttonId),
    });
  };

  const handleButtonChange = (buttonId, field, value) => {
    setTemplate({
      ...template,
      buttons: template.buttons.map((b) =>
        b.id === buttonId ? { ...b, [field]: value } : b
      ),
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setLoading(true);
        
        // Fazer upload do arquivo
        const formData = new FormData();
        formData.append("medias", file);
        formData.append("typeArch", "templates");
        
        // Enviar typeArch também como query parameter para o multer conseguir ler
        const { data } = await api.post("/whatsapp-templates/upload?typeArch=templates", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        
        // Usar a URL retornada pelo backend
        setTemplate({ ...template, headerMedia: data.url });
        toast.success("Arquivo enviado com sucesso!");
      } catch (err) {
        console.error("Erro ao fazer upload:", err);
        toast.error("Erro ao fazer upload do arquivo");
      } finally {
        setLoading(false);
      }
    }
  };

  const insertVariable = useCallback((field, variable) => {
    const variableText = `{{${variable}}}`;
    setTemplate((prev) => ({
      ...prev,
      [field]: prev[field] + variableText,
    }));
  }, []);

  // Validar proporção de variáveis vs texto (regra ESTRITA da Meta)
  const validateVariablesRatio = (text) => {
    if (!text) return { valid: true, warning: null };
    
    // Contar variáveis
    const variableRegex = /\{\{(nome|email|campo[1-5]|\d+)\}\}/gi;
    const variables = text.match(variableRegex) || [];
    const variableCount = variables.length;
    
    // Contar palavras fixas (não variáveis)
    const textWithoutVars = text.replace(variableRegex, '').trim();
    const words = textWithoutVars.split(/\s+/).filter(w => w.length > 0);
    const wordCount = words.length;
    
    // REGRA DA META: Para cada variável, precisa de pelo menos 3 palavras fixas
    // Exemplo: 1 variável = mínimo 3 palavras | 2 variáveis = mínimo 6 palavras
    const minimumWords = variableCount * 3;
    
    if (variableCount === 0) {
      return { valid: true, warning: null };
    }
    
    if (wordCount < minimumWords) {
      return {
        valid: false,
        warning: `❌ ${variableCount} variável(is) precisa(m) de pelo menos ${minimumWords} palavras fixas. Você tem apenas ${wordCount}. Adicione ${minimumWords - wordCount} palavras.`
      };
    }
    
    // Aviso se estiver próximo do limite
    const idealWords = variableCount * 4;
    if (wordCount < idealWords) {
      return {
        valid: true,
        warning: `⚠️ ${variableCount} variável(is) com ${wordCount} palavras. Recomendado: ${idealWords}+ palavras para aprovação garantida.`
      };
    }
    
    return { valid: true, warning: null };
  };

  const renderPreview = () => {
    const currentTime = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    
    return (
      <Paper className={classes.previewContainer} elevation={0}>
        <div className={classes.previewTitle}>
          <Typography variant="h6" style={{ fontWeight: 600, fontSize: '1rem' }}>
            Prévia do modelo
          </Typography>
          <IconButton 
            size="small" 
            className={classes.previewPlayButton}
            aria-label="play preview"
          >
            <PlayArrow fontSize="small" />
          </IconButton>
        </div>
        <div className={classes.previewMessage}>
          {template.headerType === "TEXT" && template.headerText && (
            <div className={classes.previewHeader}>
              {template.headerText}
            </div>
          )}
          {template.headerType === "IMAGE" && template.headerMedia && (
            <div style={{ marginBottom: 8 }}>
              <img 
                src={template.headerMedia} 
                alt="Header preview" 
                style={{ 
                  width: '100%', 
                  maxHeight: 200, 
                  objectFit: 'cover', 
                  borderRadius: 8
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = classes.previewHeader;
                  fallback.innerHTML = `🖼️ <span style="font-size: 0.75rem; color: #666; margin-left: 8px;">${template.headerMedia.split('/').pop()}</span>`;
                  e.target.parentNode.appendChild(fallback);
                }}
              />
            </div>
          )}
          {template.headerType === "VIDEO" && template.headerMedia && (
            <div style={{ marginBottom: 8 }}>
              <video 
                src={template.headerMedia} 
                controls
                style={{ 
                  width: '100%', 
                  maxHeight: 200, 
                  borderRadius: 8
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = classes.previewHeader;
                  fallback.innerHTML = `🎥 <span style="font-size: 0.75rem; color: #666; margin-left: 8px;">${template.headerMedia.split('/').pop()}</span>`;
                  e.target.parentNode.appendChild(fallback);
                }}
              />
            </div>
          )}
          {template.headerType === "DOCUMENT" && template.headerMedia && (
            <div className={classes.previewHeader} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              📄 
              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                {typeof template.headerMedia === 'string' 
                  ? template.headerMedia.split('/').pop() 
                  : template.headerMedia.name || "Mídia anexada"}
              </span>
            </div>
          )}
          {template.bodyText && (
            <div className={classes.previewBody}>
              {template.bodyText}
            </div>
          )}
          {template.footerText && (
            <div className={classes.previewFooter}>
              {template.footerText}
            </div>
          )}
          <div className={classes.previewTime}>
            {currentTime}
          </div>
          {template.buttons.length > 0 && (
            <Box mt={1}>
              {template.buttons.length > 3 && !showAllButtons ? (
                // Menu lista quando mais de 3 botões (colapsado)
                <Box 
                  className={classes.previewListMenu}
                  onClick={() => setShowAllButtons(true)}
                >
                  <ListIcon style={{ fontSize: 18 }} />
                  <span style={{ fontWeight: 600 }}>Ver {template.buttons.length} opções</span>
                </Box>
              ) : (
                // Botões expandidos
                <>
                  {template.buttons.map((button, index) => (
                    <Box 
                      key={button.id} 
                      className={classes.previewButton}
                      style={{
                        animation: showAllButtons ? `slideIn 0.3s ease ${index * 0.05}s both` : 'none'
                      }}
                    >
                      {button.type === "PHONE_NUMBER" && <Phone style={{ fontSize: 16, color: "#0088cc" }} />}
                      {button.type === "URL" && <LinkIcon style={{ fontSize: 16, color: "#0088cc" }} />}
                      {button.type === "QUICK_REPLY" && <Reply style={{ fontSize: 16, color: "#0088cc" }} />}
                      {button.type === "COPY_CODE" && <Code style={{ fontSize: 16, color: "#0088cc" }} />}
                      <span style={{ color: "#0088cc", fontSize: "0.875rem" }}>{button.text || "Botão"}</span>
                    </Box>
                  ))}
                  {template.buttons.length > 3 && showAllButtons && (
                    <Box 
                      className={classes.previewButton}
                      onClick={() => setShowAllButtons(false)}
                      style={{ backgroundColor: "#e4e6eb", marginTop: 8 }}
                    >
                      <Close style={{ fontSize: 16 }} />
                      <span style={{ fontSize: "0.875rem" }}>Fechar</span>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </div>
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        {templateId ? "Editar Template" : "Criar novo template WhatsApp"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <div className={classes.formSection}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Informações Básicas
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required variant="outlined">
                <InputLabel>Conexão WhatsApp</InputLabel>
                <Select
                  value={template.channel}
                  onChange={(e) =>
                    setTemplate({ ...template, channel: e.target.value })
                  }
                  label="Conexão WhatsApp"
                >
                  {connections.length === 0 ? (
                    <MenuItem value="" disabled>
                      Nenhuma conexão disponível
                    </MenuItem>
                  ) : (
                    connections.map((conn) => (
                      <MenuItem key={conn.id} value={conn.name}>
                        {conn.name} {conn.status === "CONNECTED" && "✓"}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome do Template"
                fullWidth
                required
                value={template.name}
                onChange={(e) => {
                  // Remove espaços e caracteres especiais, mantém apenas letras, números e underscore
                  const cleanName = e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_]/g, "_")
                    .replace(/_+/g, "_"); // Remove underscores duplicados
                  setTemplate({ ...template, name: cleanName });
                }}
                helperText="Apenas letras minúsculas, números e underscore (_). Espaços serão convertidos automaticamente."
                inputProps={{ maxLength: 512 }}
                placeholder="ex: boas_vindas_cliente"
                variant="outlined"
                error={!template.name && template.name !== ""}
              />
              <div className={classes.charCounter}>
                {template.name.length} / 512
              </div>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Idioma</InputLabel>
                <Select
                  value={template.language}
                  onChange={(e) =>
                    setTemplate({ ...template, language: e.target.value })
                  }
                  label="Idioma"
                >
                  <MenuItem value="pt_BR">🇧🇷 Português (BR)</MenuItem>
                  <MenuItem value="en_US">🇺🇸 Inglês (US)</MenuItem>
                  <MenuItem value="es_ES">🇪🇸 Espanhol</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required variant="outlined">
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={template.category}
                  onChange={(e) =>
                    setTemplate({ ...template, category: e.target.value })
                  }
                  label="Categoria"
                >
                  <MenuItem value="MARKETING">📢 Marketing</MenuItem>
                  <MenuItem value="UTILITY">🔧 Utilidade</MenuItem>
                  <MenuItem value="AUTHENTICATION">🔐 Autenticação</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box className={classes.infoBox} style={{ marginTop: 16 }}>
            <Info color="primary" fontSize="small" />
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Variáveis para Campanhas com Listas de Contatos
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                Use variáveis como <span className={classes.variableExample}>{"{{nome}}"}</span>, <span className={classes.variableExample}>{"{{email}}"}</span>, <span className={classes.variableExample}>{"{{campo1}}"}</span> até <span className={classes.variableExample}>{"{{campo5}}"}</span> para personalizar mensagens.
                As variáveis serão substituídas pelos dados da lista de contatos ao enviar a campanha.
              </Typography>
            </Box>
          </Box>

          <Divider style={{ margin: "24px 0" }} />

          <Typography variant="h6" className={classes.sectionTitle}>
            Header (Cabeçalho)
          </Typography>

          {template.headerType === "NONE" ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setTemplate({ ...template, headerType: "TEXT" })}
              fullWidth
              style={{ marginTop: 16 }}
            >
              Adicionar Header
            </Button>
          ) : (
            <>
              <FormControl component="fieldset" style={{ marginTop: 16 }}>
                <FormLabel component="legend">Tipo de Header</FormLabel>
                <RadioGroup
                  row
                  value={template.headerType}
                  onChange={(e) =>
                    setTemplate({ ...template, headerType: e.target.value })
                  }
                >
                  <FormControlLabel value="TEXT" control={<Radio />} label="Texto" />
                  <FormControlLabel value="IMAGE" control={<Radio />} label="Imagem" />
                  <FormControlLabel value="VIDEO" control={<Radio />} label="Vídeo" />
                  <FormControlLabel value="DOCUMENT" control={<Radio />} label="Documento" />
                </RadioGroup>
              </FormControl>

              {template.headerType === "TEXT" && (
                <>
                  <TextField
                    label="Texto do Header"
                    fullWidth
                    multiline
                    rows={2}
                    value={template.headerText}
                    onChange={(e) =>
                      setTemplate({ ...template, headerText: e.target.value })
                    }
                    helperText="Apenas uma variável é permitida no Header"
                    inputProps={{ maxLength: 60 }}
                    style={{ marginTop: 16 }}
                    variant="outlined"
                  />
                  <div className={classes.charCounter}>
                    {(template.headerText || "").length} / 60
                  </div>
                  
                  <Box mt={1}>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      Inserir variável no header:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {DEFAULT_VARIABLES.map((variable) => (
                        <Tooltip key={variable.key} title={variable.description}>
                          <Chip
                            label={`{{${variable.key}}}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            className={classes.variableChip}
                            onClick={() => insertVariable("headerText", variable.key)}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {["IMAGE", "VIDEO", "DOCUMENT"].includes(template.headerType) && (
                <>
                  <input
                    accept={
                      template.headerType === "IMAGE" ? "image/*" :
                      template.headerType === "VIDEO" ? "video/*" :
                      ".pdf,.doc,.docx"
                    }
                    style={{ display: "none" }}
                    id="upload-header-media"
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="upload-header-media">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUpload />}
                      className={classes.uploadButton}
                      fullWidth
                      style={{ marginTop: 16 }}
                      disabled={loading}
                    >
                      {loading ? "Enviando..." : `Upload de ${
                        template.headerType === "IMAGE" ? "Imagem" :
                        template.headerType === "VIDEO" ? "Vídeo" :
                        "Documento"
                      }`}
                    </Button>
                  </label>
                  
                  <Typography variant="caption" display="block" style={{ marginTop: 8, textAlign: "center" }}>
                    ou
                  </Typography>
                  
                  <TextField
                    label={`URL da ${
                      template.headerType === "IMAGE" ? "Imagem" :
                      template.headerType === "VIDEO" ? "Vídeo" :
                      "Documento"
                    }`}
                    fullWidth
                    value={template.headerMedia || ""}
                    onChange={(e) =>
                      setTemplate({ ...template, headerMedia: e.target.value })
                    }
                    helperText="Insira a URL pública da mídia ou faça upload acima"
                    style={{ marginTop: 8 }}
                    variant="outlined"
                    placeholder="https://exemplo.com/media.jpg"
                  />
                  
                  {template.headerMedia && (
                    <Box mt={1} p={1} bgcolor="#e8f5e9" borderRadius={1}>
                      <Typography variant="caption" display="block" style={{ wordBreak: "break-all" }}>
                        <strong>URL:</strong> {template.headerMedia}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box className={classes.infoBox} style={{ marginTop: 8 }}>
                    <Info color="primary" fontSize="small" />
                    <Typography variant="caption">
                      <strong>Importante:</strong> A URL da mídia será usada apenas quando o template for enviado nas mensagens.
                      O template será criado no Meta apenas com o tipo de mídia (Imagem/Vídeo/Documento).
                      Certifique-se de que a URL seja pública e acessível.
                    </Typography>
                  </Box>
                </>
              )}

              <Button
                variant="contained"
                className={classes.removeHeaderButton}
                startIcon={<Delete />}
                onClick={() =>
                  setTemplate({ ...template, headerType: "NONE", headerText: "", headerMedia: null })
                }
                fullWidth
                style={{ marginTop: 16 }}
              >
                Remover Header
              </Button>
            </>
          )}

          <Divider style={{ margin: "24px 0" }} />

          <Typography variant="h6" className={classes.sectionTitle}>
            Body (Corpo)
          </Typography>

          <Box className={classes.infoBox}>
            <Info color="primary" />
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>Como usar variáveis:</strong>
              </Typography>
              <Typography variant="caption" display="block">
                Use variáveis como <code className={classes.variableExample}>{"{{nome}}"}</code>,{" "}
                <code className={classes.variableExample}>{"{{email}}"}</code>,{" "}
                <code className={classes.variableExample}>{"{{campo1}}"}</code>, etc.
              </Typography>
              <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                <strong>⚠️ Regra da Meta:</strong> Para cada variável, adicione pelo menos 3 palavras de texto fixo.
              </Typography>
              <Typography variant="caption" display="block" style={{ marginTop: 2, color: '#666' }}>
                {'Exemplo OK: "Olá {{nome}}, seu pedido {{campo1}} foi confirmado!" (2 variáveis, 7 palavras ✅)'}
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Texto do Corpo"
            fullWidth
            required
            multiline
            rows={6}
            value={template.bodyText}
            onChange={(e) =>
              setTemplate({ ...template, bodyText: e.target.value })
            }
            helperText="Texto do corpo é obrigatório. Use variáveis para personalizar."
            inputProps={{ maxLength: 1024 }}
            variant="outlined"
          />
          <div className={classes.charCounter}>
            {(template.bodyText || "").length} / 1024
          </div>

          {(() => {
            const validation = validateVariablesRatio(template.bodyText);
            if (validation.warning) {
              return (
                <Box 
                  mt={1} 
                  p={1} 
                  style={{ 
                    backgroundColor: validation.valid ? '#fff3e0' : '#ffebee',
                    borderRadius: 4,
                    border: `1px solid ${validation.valid ? '#ff9800' : '#f44336'}`
                  }}
                >
                  <Typography variant="caption" style={{ color: validation.valid ? '#e65100' : '#c62828' }}>
                    {validation.warning}
                  </Typography>
                </Box>
              );
            }
            return null;
          })()}

          <Box className={classes.variablesContainer}>
            <Typography variant="subtitle2" gutterBottom>
              <Code fontSize="small" style={{ verticalAlign: "middle", marginRight: 4 }} />
              Variáveis Disponíveis
            </Typography>
            
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              Clique em uma variável para inserir no texto:
            </Typography>

            <Box className={classes.variablesList}>
              {DEFAULT_VARIABLES.map((variable) => (
                <Tooltip key={variable.key} title={variable.description}>
                  <Chip
                    label={`{{${variable.key}}}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    className={classes.variableChip}
                    onClick={() => insertVariable("bodyText", variable.key)}
                    icon={<Code fontSize="small" />}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>

          <Divider style={{ margin: "24px 0" }} />

          <Typography variant="h6" className={classes.sectionTitle}>
            Footer (Rodapé)
          </Typography>

          <TextField
            label="Texto do Rodapé"
            fullWidth
            value={template.footerText}
            onChange={(e) =>
              setTemplate({ ...template, footerText: e.target.value })
            }
            helperText="Texto que aparecerá no rodapé da mensagem"
            inputProps={{ maxLength: 60 }}
          />
          <div className={classes.charCounter}>
            {(template.footerText || "").length} / 60
          </div>

          <Divider style={{ margin: "24px 0" }} />

          <Typography variant="h6" className={classes.sectionTitle}>
            Botões
          </Typography>

          <div className={classes.buttonList}>
            {(template.buttons || []).map((button) => (
              <Paper key={button.id} className={classes.buttonItem} variant="outlined">
                <FormControl style={{ minWidth: 150 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={button.type}
                    onChange={(e) =>
                      handleButtonChange(button.id, "type", e.target.value)
                    }
                  >
                    <MenuItem value="QUICK_REPLY">Resposta Rápida (Quick Reply)</MenuItem>
                    <MenuItem value="URL">Visitar Site (Call to Action)</MenuItem>
                    <MenuItem value="PHONE_NUMBER">Ligar (Call to Action)</MenuItem>
                    <MenuItem value="COPY_CODE">Copiar Código (Call to Action)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Texto do Botão"
                  value={button.text}
                  onChange={(e) =>
                    handleButtonChange(button.id, "text", e.target.value)
                  }
                  style={{ flex: 1 }}
                />
                {button.type !== "QUICK_REPLY" && (
                  <TextField
                    label={
                      button.type === "PHONE_NUMBER" 
                        ? "Telefone" 
                        : button.type === "COPY_CODE"
                        ? "Código da oferta"
                        : "URL"
                    }
                    value={button.value}
                    onChange={(e) =>
                      handleButtonChange(button.id, "value", e.target.value)
                    }
                    style={{ flex: 1 }}
                    helperText={
                      button.type === "COPY_CODE" 
                        ? "Código que será copiado automaticamente"
                        : ""
                    }
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => handleRemoveButton(button.id)}
                  color="secondary"
                >
                  <Delete />
                </IconButton>
              </Paper>
            ))}
          </div>

          {template.buttons.length < 10 && (
            <>
              <FormControl fullWidth variant="outlined" style={{ marginTop: 16 }}>
                <InputLabel id="add-button-label">Selecione o tipo de botão</InputLabel>
                <Select
                  labelId="add-button-label"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddButton(e.target.value);
                    }
                  }}
                  label="Selecione o tipo de botão"
                >
                  <MenuItem value="" disabled>
                    Selecione o tipo de botão
                  </MenuItem>
                  <MenuItem value="QUICK_REPLY">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Reply fontSize="small" />
                      Resposta Rápida (Quick Reply)
                    </Box>
                  </MenuItem>
                  <Divider />
                  <MenuItem value="URL">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinkIcon fontSize="small" />
                      Visitar Site (Call to Action)
                    </Box>
                  </MenuItem>
                  <MenuItem value="PHONE_NUMBER">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone fontSize="small" />
                      Ligar (Call to Action)
                    </Box>
                  </MenuItem>
                  <MenuItem value="COPY_CODE">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Code fontSize="small" />
                      Copiar Código (Call to Action)
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              <Box className={classes.infoBox} style={{ marginTop: 16 }}>
                <Info color="primary" fontSize="small" />
                <Box>
                  <Typography variant="caption" display="block">
                    <strong>Tipos de botões:</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>Quick Reply:</strong> Resposta rápida (máx. 3)
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>Call to Action:</strong> Ação como visitar site ou ligar (máx. 2)
                  </Typography>
                  <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                    {template.buttons.length > 3 ? "⚠️ Mais de 3 botões serão exibidos como menu lista" : ""}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </div>

        <div className={classes.previewSection}>{renderPreview()}</div>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading || !template.name || !template.channel || !template.bodyText}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? "Enviando para Meta..." : "Enviar para Meta"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateModal;
